import { useMutation, useQuery } from '@tanstack/react-query';
import { useNotify } from '@/components/snackbar';
import PropTypes from 'prop-types';
import { createContext, useMemo, useState } from 'react';
import { isWithinInterval, parseISO } from 'date-fns';
import { useParams } from 'react-router';
import { saveCheckout } from '@/services/checkout-builder';
import { getCheckout, validateCouponService } from '@/services/product-checkout';
import { getProductService, getPaymentMethods } from '@/services/products';
import { CACHE_TIME, STALE_TIME } from '@/services/constants';
import { FormScopes } from '@/utils/form';
import { lightThemeValues } from '@/sections/@checkout-builder/constants';
import { buildSaveErrorToast } from '@/sections/@checkout-builder/utils/buildSaveErrorToast';
import { CheckoutBuilderHoverProvider } from './CheckoutBuilderHoverContext';

const CheckoutBuilderContext = createContext({
  device: 'desktop',
  setDevice: () => {},
  scope: FormScopes.INDEX,
  setScope: () => {},
  update: () => {},
  updating: false,
  checkout: null,
  checkouts: [],
  loading: false,
  paymentMethod: '',
  setPaymentMethod: () => {},
  product: {},
  selectedBumps: [],
  setSelectedBumps: () => {},
  couponData: undefined,
  setCouponData: () => {},
  validateCoupon: async () => {},
  isLoadingValidateCoupon: false,
  isLoadingProduct: false,
});

const CheckoutBuilderProvider = ({ children }) => {
  const notify = useNotify();

  const [device, setDevice] = useState('desktop');

  const [scope, setScope] = useState(FormScopes.INDEX);

  const [paymentMethod, setPaymentMethod] = useState('');

  const [selectedBumps, setSelectedBumps] = useState([]);

  const [couponData, setCouponData] = useState(0);

  const { id } = useParams();

  const { data: checkout, isLoading: loading } = useQuery({
    queryKey: ['checkout-builder', id],
    queryFn: () =>
      getCheckout({
        id,
      }),
    select(data) {
      if (!data?.config) return data;

      const mobileSettings = data.config.mobile?.settings?.text?.color?.primary?.length
        ? {
            ...lightThemeValues,
            ...data?.config?.mobile?.settings,
          }
        : lightThemeValues;
      return {
        ...data,
        config: {
          ...data.config,
          mobile: {
            ...data?.config?.mobile,
            settings: {
              ...mobileSettings,
            },
          },
          desktop: {
            ...data.config.desktop,
            settings: {
              ...lightThemeValues,
              ...data?.config?.desktop?.settings,
            },
          },
        },
      };
    },
    enabled: !!id,
    initialData: null,
  });

  // `mutate`, e não `mutateAsync`: o `onError` já mostra a recusa e o builder não espera o save.
  // Com a promise, todo save recusado virava rejeição sem tratamento no navegador.
  const { mutate: update, isLoading: updating } = useMutation({
    mutationFn: (data) => saveCheckout({ ...data, id }),
    onSuccess: () => {
      notify.success('Checkout salvo com sucesso!');
    },
    onError: (error) => {
      const { message, subtitle } = buildSaveErrorToast(error);
      notify.error(message, { subtitle });
    },
  });

  const productId = checkout?.product?.id;

  const { data: product, isLoading: isLoadingProduct } = useQuery(
    ['product', { productId }],
    async () => {
      const result = await getProductService({ id: productId });
      const paymentMethods = await getPaymentMethods();

      const paymentsOrder = result?.paymentsOrder?.length
        ? result.paymentsOrder
        : paymentMethods?.map(({ type }) => type);

      return {
        ...result,
        paymentsOrder,
      };
    },
    {
      enabled: !!productId,
      staleTime: STALE_TIME,
      cacheTime: CACHE_TIME,
      select(data) {
        return {
          ...data,
          offers: data.offers?.map((offer) => ({
            ...offer,
            offerId: offer.id,
          })),
          bumps: data.bumps?.sort((a, b) => a.position - b.position),
        };
      },
    }
  );

  const productForDisplay = useMemo(() => {
    if (!product) return product;
    if (!checkout?.offers?.length || !product?.offers?.length) return product;
    const checkoutOffer = checkout.offers[0];
    const match = product.offers.find(
      (o) => o.id === checkoutOffer.id || o.offerId === checkoutOffer.id
    );
    if (!match) return product;
    const displayPrice =
      typeof checkoutOffer.price === 'number' ? checkoutOffer.price : match.price;
    const offersForDisplay = product.offers.map((o) => ({
      ...o,
      default: o.id === checkoutOffer.id || o.offerId === checkoutOffer.id,
    }));
    return {
      ...product,
      price: displayPrice,
      offers: offersForDisplay,
    };
  }, [checkout, product]);

  const { mutateAsync: validateCoupon, isLoading: isLoadingValidateCoupon } = useMutation({
    mutationFn: (coupon) =>
      validateCouponService({
        code: coupon,
        offerId: productForDisplay?.offers?.find((offer) => offer.default)?.id?.split('_')[0],
      }),
    onSuccess: (data) => {
      const { startTime, endTime } = data;
      const now = new Date();
      const lifetimeDate = new Date(8640000000000000);

      if (
        isWithinInterval(now, {
          start: parseISO(startTime),
          end: endTime ? parseISO(endTime) : lifetimeDate,
        })
      ) {
        setCouponData(data);
      } else {
        throw new Error('Cupom inválido');
      }
    },
    onError() {
      setCouponData(undefined);
    },
  });

  const value = useMemo(
    () => ({
      device,
      setDevice,
      scope,
      setScope,
      update,
      updating,
      checkout,
      loading,
      paymentMethod,
      setPaymentMethod,
      product: productForDisplay,
      selectedBumps,
      setSelectedBumps,
      validateCoupon,
      couponData,
      setCouponData,
      isLoadingValidateCoupon,
      isLoadingProduct,
    }),
    [
      checkout,
      device,
      loading,
      scope,
      update,
      updating,
      paymentMethod,
      setPaymentMethod,
      productForDisplay,
      selectedBumps,
      setSelectedBumps,
      validateCoupon,
      couponData,
      setCouponData,
      isLoadingValidateCoupon,
      isLoadingProduct,
    ]
  );

  return (
    <CheckoutBuilderContext.Provider value={value}>
      <CheckoutBuilderHoverProvider>{children}</CheckoutBuilderHoverProvider>
    </CheckoutBuilderContext.Provider>
  );
};

CheckoutBuilderProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export { CheckoutBuilderContext, CheckoutBuilderProvider };
